/**
 * LLM Access Policy — which AI services each endpoint may reach.
 *
 * Standing rules only. Time-bound access lives on the JIT Access page, and the
 * two are kept apart deliberately: a rule here stays until someone changes it,
 * a grant there ends by itself. Conflating them is how an operator ends up
 * believing access has lapsed when it is in fact permanent.
 *
 * Scope is either the organisation default (every endpoint) or one device. A
 * device rule overrides the org default; "Inherit" clears the device rule and
 * hands the decision back to the org.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as directory from '../lib/directoryApi';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '../components/ui';
import * as api from '../lib/targetAccessApi';
import type { EffectiveTarget, TargetMode } from '../lib/targetAccessApi';
import { usePermission } from '../hooks/usePermission';
import AccountPolicyPanel from '../components/AccountPolicyPanel';
import { useTranslation } from 'react-i18next';

interface Props {
  apiUrl: string;
}

interface Device {
  device_id: string;
  hostname?: string | null;
  os?: string | null;
  status?: string;
}

const RISK_ORDER = ['high_risk', 'unsanctioned', 'tolerated', 'sanctioned'];
const RISK_LABEL: Record<string, string> = {
  high_risk: 'High risk',
  unsanctioned: 'Unsanctioned',
  tolerated: 'Tolerated',
  sanctioned: 'Sanctioned',
};
const RISK_BADGE: Record<string, string> = {
  high_risk: 'badge-error',
  unsanctioned: 'badge-redact',
  tolerated: 'badge-info',
  sanctioned: 'badge-success',
};
const MODE_LABEL: Record<TargetMode, string> = { allow: 'pages.llmAccessPolicy.modeAllow', deny: 'pages.llmAccessPolicy.modeDeny', inherit: 'pages.llmAccessPolicy.modeInherit' };
const MODES: TargetMode[] = ['allow', 'deny', 'inherit'];

/**
 * Emoji icons per provider — same lightweight approach gatei5 uses for its app
 * tiles (no brand SVGs, just a recognisable glyph per vendor/surface). Falls
 * back to a generic icon by provider id pattern when a vendor isn't listed.
 */
const PROVIDER_ICON: Record<string, string> = {
  openai: '🤖',
  'chatgpt-desktop': '🤖',
  anthropic: '🧠',
  'claude-desktop': '🧠',
  'google-gemini': '✨',
  'google-gemini-web': '✨',
  'google-vertex': '✨',
  'google-workspace-ai': '✨',
  'azure-openai': '🔷',
  'aws-bedrock': '🪨',
  'github-copilot': '🐙',
  github: '🐙',
  'cursor-desktop': '⌨️',
  'm365-copilot': '🪟',
  cohere: '🌊',
  mistral: '🌬️',
  groq: '⚡',
  openrouter: '🔀',
  huggingface: '🤗',
  perplexity: '❓',
  'perplexity-agent': '❓',
  together: '🧩',
  deepseek: '🐳',
  portkey: '🔑',
  'cloudflare-ai': '☁️',
  ollama: '🦙',
  'lm-studio': '🖥️',
  llamacpp: '🦙',
  localai: '🖥️',
  'text-generation-webui': '🖥️',
  'notion-ai': '📝',
  'salesforce-einstein': '☁️',
  'x-ai': '❌',
  'z-ai': '⚫',
};
function providerIcon(providerId: string, type?: string): string {
  return PROVIDER_ICON[providerId] || (type === 'mcp' ? '🔌' : '🤖');
}

/**
 * Curated ordering for the most-asked-about providers — the ones an admin sets
 * a policy for on day one. Everything else stays in the risk-grouped tables
 * below, sorted by risk (defence-in-depth ordering, not popularity), which is
 * still the right default for the long tail nobody's heard of.
 */
const POPULAR_ORDER = [
  'openai',
  'anthropic',
  'claude-desktop',
  'google-gemini',
  'google-gemini-web',
  'azure-openai',
  'google-vertex',
  'aws-bedrock',
  'chatgpt-desktop',
  'github-copilot',
  'cursor-desktop',
  'm365-copilot',
];

const LlmAccessPolicy: React.FC<Props> = ({ apiUrl }) => {
  const { t: tr } = useTranslation();
  const canWrite = usePermission('update:policy');
  const [tab, setTab] = useState<'providers' | 'account'>('providers');

  // `?device=<id>` lets the Endpoint Agents page link straight to one endpoint's
  // rules instead of dropping the operator on the org default and making them
  // find the row again.
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState<Device[]>([]);
  const [scope, setScope] = useState<string>(searchParams.get('device') || api.ORG_SCOPE);
  const [targets, setTargets] = useState<EffectiveTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [userDraft, setUserDraft] = useState<Record<string, string>>({});
  const [groupDraft, setGroupDraft] = useState<Record<string, string>>({});
  // Synced AD groups for the group picker. Empty when no directory is
  // connected — the picker then points at the Directory page instead.
  const [adGroups, setAdGroups] = useState<directory.DirectoryGroup[]>([]);
  useEffect(() => {
    directory.listGroups(apiUrl, '', 500).then(setAdGroups).catch(() => setAdGroups([]));
  }, [apiUrl]);
  const groupByName = useMemo(() => new Map(adGroups.map((g) => [g.name.toLowerCase(), g])), [adGroups]);

  const isOrgScope = scope === api.ORG_SCOPE;

  useEffect(() => {
    fetch(`${apiUrl}/admin/bdr/devices`)
      .then((r) => r.json())
      .then((d) => setDevices(Array.isArray(d.devices) ? d.devices : []))
      .catch(() => setDevices([]));
  }, [apiUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listTargets(apiUrl, scope);
      setTargets(data.targets);
    } catch (e: any) {
      setError(e.message || 'Could not load LLM access policy');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const mutate = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      setNote(msg);
      setTimeout(() => setNote(null), 4000);
    } catch (e: any) {
      setError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const setMode = (providerId: string, mode: TargetMode, routingMode?: api.RoutingMode) =>
    mutate(
      () => api.saveTargets(apiUrl, scope, [{ providerId, mode, routingMode }]),
      `Saved. Endpoints apply it on their next check-in.`
    );

  const setRouting = (providerId: string, routingMode: api.RoutingMode) =>
    mutate(
      () => api.saveTargets(apiUrl, scope, [{ providerId, mode: 'allow', routingMode }]),
      routingMode === 'tunnel'
        ? 'Traffic will now tunnel through Agenti5.'
        : 'Traffic will connect directly again (MITM-inspected, not tunnelled).'
    );

  const setUserRule = (providerId: string, userUpn: string, mode: TargetMode, label = userUpn) =>
    mutate(
      () => api.saveTargets(apiUrl, scope, [{ providerId, userUpn, mode }]),
      mode === 'inherit' ? `Cleared the rule for ${label}.` : `${tr(MODE_LABEL[mode])} set for ${label}.`
    );

  const matchesFilter = useCallback(
    (t: EffectiveTarget) => {
      const q = filter.trim().toLowerCase();
      return !q || t.label.toLowerCase().includes(q) || t.hosts.some((h) => h.includes(q));
    },
    [filter]
  );

  // The handful of providers most admins configure first — pulled out of the
  // risk-ordered groups (which bury them under "High risk"/"Unsanctioned") and
  // shown as a friendly grid up top, in a fixed, recognisable order.
  const popular = useMemo(() => {
    const byId = new Map(targets.filter(matchesFilter).map((t) => [t.providerId, t]));
    return POPULAR_ORDER.map((id) => byId.get(id)).filter((t): t is EffectiveTarget => !!t);
  }, [targets, matchesFilter]);
  const popularIds = useMemo(() => new Set(popular.map((t) => t.providerId)), [popular]);

  const grouped = useMemo(() => {
    const byRisk = new Map<string, EffectiveTarget[]>();
    for (const t of targets.filter((x) => matchesFilter(x) && !popularIds.has(x.providerId))) {
      const key = t.riskLevel || 'unsanctioned';
      byRisk.set(key, [...(byRisk.get(key) || []), t]);
    }
    return RISK_ORDER.filter((r) => byRisk.has(r)).map((r) => ({ risk: r, items: byRisk.get(r)! }));
  }, [targets, matchesFilter, popularIds]);
  const otherCount = grouped.reduce((n, g) => n + g.items.length, 0);

  const [showOthers, setShowOthers] = useState(false);
  const othersVisible = showOthers || filter.trim().length > 0;

  const configuredCount = targets.filter((t) => t.mode !== 'inherit' || t.userRules.length > 0).length;

  // Shared "Rule" cell — mode buttons, per-user overrides, and the add-user-rule
  // form. Used by both the Popular grid and the risk-grouped tables so the two
  // never drift out of sync.
  const RuleEditor: React.FC<{ t: EffectiveTarget }> = ({ t }) => {
    const { t: tr } = useTranslation();
    return (
    <div>
      <div style={{ whiteSpace: 'nowrap' }}>
        {MODES.filter((m) => !(isOrgScope && m === 'inherit')).map((m) => (
          <button
            key={m}
            className={`ent-btn sm ${t.mode === m ? 'primary' : ''}`}
            disabled={!canWrite || busy}
            style={{ marginRight: 4 }}
            onClick={() => setMode(t.providerId, m, m === 'allow' ? t.routing : undefined)}
            title={
              m === 'inherit'
                ? `Fall back to the organisation default (${tr(MODE_LABEL[t.orgMode])})`
                : `${tr(MODE_LABEL[m])} ${t.label}`
            }
          >
            {tr(MODE_LABEL[m])}
          </button>
        ))}
        {!isOrgScope && t.mode === 'inherit' && (
          <span style={{ fontSize: 10, color: 'var(--text-2)', marginLeft: 4 }}>
            {tr('pages.llmAccessPolicy.org')} {tr(MODE_LABEL[t.orgMode])}
            {t.orgMode === 'allow' && t.orgRouting === 'tunnel' ? ` (${tr('pages.llmAccessPolicy.tunnelled')})` : ''}
          </span>
        )}
      </div>

      {/* Routing: only meaningful once this row is actually allowing traffic. */}
      {t.mode === 'allow' && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{tr('pages.llmAccessPolicy.routeVia')}</span>
          {(['direct', 'tunnel'] as api.RoutingMode[]).map((r) => (
            <button
              key={r}
              className={`ent-btn sm ${t.routing === r ? 'primary' : ''}`}
              disabled={!canWrite || busy}
              onClick={() => setRouting(t.providerId, r)}
              title={
                r === 'direct'
                  ? tr('pages.llmAccessPolicy.endpointMITMProxyForwardsStraightTo')
                  : tr('pages.llmAccessPolicy.endpointForwardsThroughTheAgenti5Firewall')
              }
            >
              {r === 'direct' ? tr('pages.llmAccessPolicy.directMITM') : tr('pages.llmAccessPolicy.tunnelViaAgenti5')}
            </button>
          ))}
        </div>
      )}

      {/* Per-user and per-AD-group overrides. Precedence on the endpoint:
          a user rule beats a group rule beats the everyone rule above. */}
      {t.userRules.map((u) => {
        const isGroup = directory.isGroupSubject(u.userUpn);
        const label = isGroup ? `AD group ${u.groupName || u.userUpn.slice(6)}` : u.userUpn;
        return (
        <div key={u.userUpn} style={{ fontSize: 11, marginTop: 5, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <Icon name={isGroup ? 'server' : 'users'} />
          {isGroup ? (
            <span title={u.userUpn.slice(6)}>
              <strong>{u.groupName || tr('pages.llmAccessPolicy.unknownGroup')}</strong>
              <span style={{ fontSize: 9, color: 'var(--text-2)', marginLeft: 4 }}>{tr('pages.llmAccessPolicy.adGroup')}</span>
            </span>
          ) : (
            u.userUpn
          )}
          :{' '}
          <span className={`badge ${u.mode === 'deny' ? 'badge-error' : 'badge-success'}`}>
            {tr(MODE_LABEL[u.mode])}
          </span>
          {u.mode === 'allow' && u.routing === 'tunnel' && (
            <span style={{ fontSize: 10, color: 'var(--text-2)', marginLeft: 4 }}>{tr('pages.llmAccessPolicy.tunnelled')}</span>
          )}
          {canWrite && (
            <button
              className="ent-btn sm"
              disabled={busy}
              style={{ marginLeft: 6 }}
              title={tr('pages.llmAccessPolicy.clearRuleFor', { label })}
              onClick={() => setUserRule(t.providerId, u.userUpn, 'inherit', label)}
            >
              <Icon name="close" />
            </button>
          )}
        </div>
        );
      })}

      {canWrite && (
        <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
          <input
            className="ent-input"
            style={{ fontSize: 11, padding: '2px 6px', maxWidth: 170 }}
            placeholder={tr('pages.llmAccessPolicy.userCorpCom')}
            value={userDraft[t.providerId] || ''}
            onChange={(e) => setUserDraft((m) => ({ ...m, [t.providerId]: e.target.value }))}
          />
          {(['allow', 'deny'] as TargetMode[]).map((m) => (
            <button
              key={m}
              className="ent-btn sm"
              disabled={busy || !(userDraft[t.providerId] || '').trim()}
              onClick={() => {
                const u = (userDraft[t.providerId] || '').trim();
                setUserDraft((d) => ({ ...d, [t.providerId]: '' }));
                void setUserRule(t.providerId, u, m);
              }}
              title={tr('pages.llmAccessPolicy.modeThisUser', { mode: tr(MODE_LABEL[m]) })}
            >
              {tr(MODE_LABEL[m])}
            </button>
          ))}
        </div>
      )}

      {canWrite && adGroups.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <input
            className="ent-input"
            style={{ fontSize: 11, padding: '2px 6px', maxWidth: 170 }}
            placeholder={tr('pages.llmAccessPolicy.adGroup2')}
            list="ad-group-options"
            value={groupDraft[t.providerId] || ''}
            onChange={(e) => setGroupDraft((m) => ({ ...m, [t.providerId]: e.target.value }))}
          />
          {(['allow', 'deny'] as TargetMode[]).map((m) => {
            const g = groupByName.get((groupDraft[t.providerId] || '').trim().toLowerCase());
            return (
              <button
                key={m}
                className="ent-btn sm"
                disabled={busy || !g}
                onClick={() => {
                  if (!g) return;
                  setGroupDraft((d) => ({ ...d, [t.providerId]: '' }));
                  void setUserRule(t.providerId, directory.groupSubject(g.sid), m, `AD group ${g.name}`);
                }}
                title={g ? `${tr(MODE_LABEL[m])} every member of ${g.name}` : tr('pages.llmAccessPolicy.pickASyncedADGroup')}
              >
                {tr(MODE_LABEL[m])}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
  };

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <datalist id="ad-group-options">
            {adGroups.map((g) => (
              <option key={g.sid} value={g.name}>
                {g.memberCount != null ? `${g.memberCount} members` : ''}
              </option>
            ))}
          </datalist>
          <h1>{tr('pages.llmAccessPolicy.llmAccessPolicy')}</h1>
          <p className="subtitle">
            {tr('pages.llmAccessPolicy.whichAIServicesEachEndpointMay')} <strong>{tr('pages.llmAccessPolicy.jitAccess')}</strong>.
          </p>
        </div>
      </div>

      <div className="ent-toolbar" style={{ gap: 8, marginBottom: 12 }}>
        <button className={`ent-btn${tab === 'providers' ? ' primary' : ''}`} onClick={() => setTab('providers')}>
          {tr('pages.llmAccessPolicy.providerAccess')}
        </button>
        <button className={`ent-btn${tab === 'account' ? ' primary' : ''}`} onClick={() => setTab('account')}>
          {tr('pages.llmAccessPolicy.accountPolicy')}
        </button>
      </div>

      {tab === 'account' && <AccountPolicyPanel apiUrl={apiUrl} />}
      {tab === 'providers' && (
      <>
      {error && (
        <div className="notice error">
          <Icon name="warning" /> {error}
        </div>
      )}
      {note && (
        <div className="notice">
          <Icon name="checkCircle" /> {note}
        </div>
      )}

      {/* Scope */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{tr('pages.llmAccessPolicy.scope')}</div>
        <div className="ent-card-body">
          <div className="ent-toolbar" style={{ marginTop: 0, gap: 10, flexWrap: 'wrap' }}>
            <select
              className="ent-input"
              style={{ maxWidth: 380 }}
              value={scope}
              onChange={(e) => {
                const next = e.target.value;
                setScope(next);
                // Keep the URL honest so the page can be shared or reloaded.
                setSearchParams(next === api.ORG_SCOPE ? {} : { device: next }, { replace: true });
              }}
            >
              <option value={api.ORG_SCOPE}>{tr('pages.llmAccessPolicy.organisationDefaultEveryEndpoint')}</option>
              {devices.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {d.hostname || d.device_id}
                  {d.os ? ` (${d.os})` : ''}
                </option>
              ))}
            </select>
            <input
              className="ent-input"
              style={{ maxWidth: 240 }}
              placeholder={tr('pages.llmAccessPolicy.filterServices')}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button className="ent-btn" disabled={loading} onClick={() => void load()}>
              <Icon name="refresh" className={loading ? 'spin' : ''} /> {tr('pages.llmAccessPolicy.refresh')}
            </button>
            <span style={{ color: 'var(--text-2)', fontSize: 12 }}>{tr('pages.llmAccessPolicy.ofServicesConfigured', { v0: configuredCount, v1: targets.length })}</span>
          </div>
          <p className="subtitle" style={{ marginBottom: 0 }}>
            {isOrgScope ? tr('pages.llmAccessPolicy.orgScopeIntro') : tr('pages.llmAccessPolicy.endpointScopeIntro')}{' '}
            {tr('pages.llmAccessPolicy.changesReachEndpoints')}
          </p>
        </div>
      </div>

      {!canWrite && (
        <div className="notice">
          <Icon name="lock" /> {tr('pages.llmAccessPolicy.youCanViewThisPolicyBut')}
        </div>
      )}

      {loading ? (
        <div className="ent-card">
          <div className="ent-card-body">{tr('pages.llmAccessPolicy.loading')}</div>
        </div>
      ) : (
        <>
          {popular.length > 0 && (
            <div className="ent-card" style={{ marginBottom: 16 }}>
              <div className="ent-card-head">
                {tr('pages.llmAccessPolicy.popular')}
                <span style={{ marginLeft: 8, color: 'var(--text-2)', fontWeight: 400 }}>
                  {tr('pages.llmAccessPolicy.theServicesMostOrgsSetA')}
                </span>
              </div>
              <div className="ent-card-body">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 12,
                  }}
                >
                  {popular.map((t) => (
                    <div
                      key={t.providerId}
                      className="ent-table-wrap"
                      style={{ padding: 12, borderRadius: 10 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 20, lineHeight: 1 }}>
                          {providerIcon(t.providerId, t.type)}
                        </span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{t.label}</div>
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--text-2)',
                              fontFamily: 'monospace',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={t.hosts.join(', ')}
                          >
                            {t.hosts[0]}
                            {t.hosts.length > 1 ? ` +${t.hosts.length - 1}` : ''}
                          </div>
                        </div>
                        <span className={`badge ${RISK_BADGE[t.riskLevel || 'unsanctioned']}`} style={{ fontSize: 9 }}>
                          {RISK_LABEL[t.riskLevel || 'unsanctioned']}
                        </span>
                      </div>
                      {t.grants.length > 0 && (
                        <div style={{ fontSize: 10, color: 'var(--text-2)', marginBottom: 6 }}>
                          <Icon name="clock" /> {tr('pages.llmAccessPolicy.activeGrants', { count: t.grants.length })} {tr('pages.llmAccessPolicy.seeJitAccess')}
                        </div>
                      )}
                      <RuleEditor t={t} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {otherCount > 0 && (
            <button
              className="ent-btn sm"
              style={{ marginBottom: 16 }}
              onClick={() => setShowOthers((v) => !v)}
            >
              <span
                style={{
                  display: 'inline-block',
                  transform: othersVisible ? 'rotate(180deg)' : undefined,
                }}
              >
                <Icon name="chevronDown" />
              </span>{' '}
              {tr(othersVisible ? 'pages.llmAccessPolicy.hide' : 'pages.llmAccessPolicy.show')} {tr('pages.llmAccessPolicy.moreProviders', { count: otherCount })}
            </button>
          )}

          {othersVisible &&
            grouped.map(({ risk, items }) => (
          <div className="ent-card" key={risk} style={{ marginBottom: 16 }}>
            <div className="ent-card-head">
              <span className={`badge ${RISK_BADGE[risk] || 'badge-info'}`}>{RISK_LABEL[risk] || risk}</span>
              <span style={{ marginLeft: 8, color: 'var(--text-2)', fontWeight: 400 }}>
                {tr('pages.llmAccessPolicy.serviceCount', { count: items.length })}
              </span>
            </div>
            <div className="ent-card-body">
              <div className="ent-table-wrap">
                <table className="ent-table">
                  <thead>
                    <tr>
                      <th>{tr('pages.llmAccessPolicy.service')}</th>
                      <th>{tr('pages.llmAccessPolicy.destinations')}</th>
                      <th style={{ width: 300 }}>{tr('pages.llmAccessPolicy.rule')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((t) => (
                      <tr key={t.providerId}>
                        <td>
                          <span style={{ marginRight: 6 }}>{providerIcon(t.providerId, t.type)}</span>
                          <strong>{t.label}</strong>
                          {t.grants.length > 0 && (
                            <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 3 }}>
                              <Icon name="clock" /> {tr('pages.llmAccessPolicy.activeGrants', { count: t.grants.length })} {tr('pages.llmAccessPolicy.seeJitAccess')}
                            </div>
                          )}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-2)' }}>
                          {t.hosts.slice(0, 3).join(', ')}
                          {t.hosts.length > 3 ? ` +${t.hosts.length - 3}` : ''}
                          {t.apps.length > 0 && (
                            <div style={{ marginTop: 2 }}>{tr('pages.llmAccessPolicy.appsList', { list: t.apps.join(', ') })}</div>
                          )}
                        </td>
                        <td>
                          <RuleEditor t={t} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
            ))}
        </>
      )}

      {!loading && popular.length === 0 && grouped.length === 0 && (
        <div className="ent-card">
          <div className="ent-card-body ent-empty">
            {filter ? tr('pages.llmAccessPolicy.noServicesMatchThatFilter') : tr('pages.llmAccessPolicy.theProviderCatalogueIsEmpty')}
          </div>
        </div>
      )}

      <p className="subtitle" style={{ fontSize: 11 }}>
        <Icon name="info" /> {tr('pages.llmAccessPolicy.perUserRulesAreEnforcedBy')}
      </p>
      </>
      )}
    </div>
  );
};

export default LlmAccessPolicy;
